import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useProfile } from "../../../contexts/ProfileContext";
import {
  fetchDefaultMembers,
  fetchTeamMembers,
  updateTeamMemberRole,
} from "../../../store/slices/memberSlice";
import CardTable from "../../../components/common/CardTable";
import { TableRow } from "../../../components/common/Table/Table";
import MembersTable from "./MembersTable";
import { DropdownOption } from "../../../components/common/Dropdown";
import { defaultedMembersColumns } from "./membersColumns";
import { mapRowsWithDropdown } from "./membersHelpers";

export interface DefaultedMembersTabProps {
  defaultMembers: TableRow[];
  defaultPage: number;
  setDefaultPage: (page: number) => void;
  totalItems?: number;
  showSingleBanner?: boolean;
  sortColumn: string;
  sortDirection: "asc" | "desc";
  setSortColumn: (col: string) => void;
  setSortDirection: (dir: "asc" | "desc") => void;
  teamId?: string;
  /** Called after a successful team-role update so the parent can refresh the UBT Membership Summary. */
  onRoleUpdated?: () => void;
}

/**
 * Renders defaulted members with role management and pagination.
 * Refetches team members and contacts after role changes to ensure email lists update in real-time.
 * @param props - Defaulted members table state and handlers.
 * @returns The defaulted members tab content.
 */
const DefaultedMembersTab: React.FC<DefaultedMembersTabProps> = ({
  defaultMembers,
  defaultPage,
  setDefaultPage,
  totalItems,
  showSingleBanner,
  sortColumn,
  sortDirection,
  setSortColumn,
  setSortDirection,
  teamId,
  onRoleUpdated,
}) => {
  const ipp = 10;
  const { profile } = useProfile();
  const isAdmin = profile?.isAdmin;
  const loginNuid = profile?.nuid ? String(profile.nuid) : null;
  const dispatch = useAppDispatch();
  const memberAccess = useAppSelector((state) => state.membersSlice.memberAccess);
  const normalizedMemberAccess =
    (memberAccess as any)?.data ??
    (memberAccess as any)?.access ??
    memberAccess;
  const canMemberShipEdit = Boolean(
    normalizedMemberAccess?.canMemberShipEdit ??
      normalizedMemberAccess?.canMembershipEdit ??
      false,
  );

  /**
   * Handles member role changes and refreshes both member list and contacts for real-time email updates.
   * @param row - The member row being updated.
   * @param selected - The newly selected role option.
   */
  const handleRoleChange = async (row: any, selected: DropdownOption) => {
    if (!teamId) return;

    const updateResult: {
      meta?: { requestStatus?: string };
      type?: string;
    } = await dispatch(
      updateTeamMemberRole({
        memberType: "default",
        nuid: row.nuid,
        teamId,
        newStatus: selected.value,
        currentStatus: row.teamRole,
        startDate: row.startdate,
        deptPk: row.deptPk,
        privStartDate: row.privlegestartdate,
      }),
    );

    const isUpdateSuccessful =
      updateResult?.meta?.requestStatus === "fulfilled" ||
      updateResult?.type === "members/updateTeamMemberRole/fulfilled" ||
      updateResult?.type?.endsWith("/fulfilled") === true;

    if (!isUpdateSuccessful) {
      return;
    }

    const apiSortColumn =
      sortColumn === "lastName" ? "LAST_NAME" : "FIRST_NAME";
    const apiSortOrder = sortDirection.toUpperCase() as "ASC" | "DESC";

    // If the current page had more than one record the page is still populated
    // after this removal — re-fetch the same page (page state won't change so
    // the parent useEffect won't fire; we dispatch directly).
    // If it had exactly one record the page is now empty — move back one page
    // and let the parent's useEffect fire fetchDefaultMembers for that page.
    const currentRowCount = (defaultMembers ?? []).length;
    if (currentRowCount > 1) {
      dispatch(
        fetchDefaultMembers({
          teamId,
          sortColumn: apiSortColumn,
          sortOrder: apiSortOrder,
          page: defaultPage,
          per_page: ipp,
        }),
      );
    } else if (defaultPage > 1) {
      // Last member on this page removed; parent useEffect will fetch page - 1.
      setDefaultPage(defaultPage - 1);
    } else {
      // Last member on page 1 removed; dispatch directly (page state unchanged).
      dispatch(
        fetchDefaultMembers({
          teamId,
          sortColumn: apiSortColumn,
          sortOrder: apiSortOrder,
          page: 1,
          per_page: ipp,
        }),
      );
    }

    // Refetch team members to update contacts and sponsor email lists in real-time
    dispatch(fetchTeamMembers({ teamId }));

    // Notify parent to refresh the UBT Membership Summary counts
    onRoleUpdated?.();
  };

  const handleSort = (columnKey: string, direction: "ASC" | "DESC") => {
    const dirMap: Record<"ASC" | "DESC", "asc" | "desc"> = {
      ASC: "asc",
      DESC: "desc",
    };
    const normalized = dirMap[direction] ?? "asc";
    setSortColumn(columnKey);
    setSortDirection(normalized);
  };

  const pagedRows = defaultMembers ?? [];
  const memberRoleOptionsDefault = useAppSelector(
    (state) => state.membersSlice.memberRoleOptionsDefault ?? [],
  );
  const memberRoleOptionsLoading = useAppSelector(
    (state) => state.membersSlice.memberRoleOptionsLoading,
  );
  const updateMemberRoleLoading = useAppSelector(
    (state) => state.membersSlice.updateMemberRoleLoading,
  );
  const displayRows = mapRowsWithDropdown(
    pagedRows,
    isAdmin,
    "default-row",
    memberRoleOptionsDefault,
    handleRoleChange,
    {
      canMemberShipEdit,
      loginNuid,
      requireAdminForDropdown: false,
    },
  );

  const defaultMembersLoading = useAppSelector(
    (state) => state.membersSlice.defaultMembersLoading,
  );
  const defaultMembersError = useAppSelector(
    (state) => state.membersSlice.defaultMembersError,
  );

  return (
    <CardTable>
      <MembersTable
        displayRows={displayRows}
        totalItems={totalItems}
        itemsPerPage={ipp}
        currentPage={defaultPage}
        onPageChange={(p: number) => setDefaultPage(p)}
        columns={defaultedMembersColumns}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        loading={defaultMembersLoading}
        optionsLoading={memberRoleOptionsLoading}
        error={defaultMembersError}
        showSingleBanner={showSingleBanner}
        updateRoleLoading={updateMemberRoleLoading}
        paginationTestId="pagination-default"
        paginationWrapperTestId="pagination-default-wrapper"
      />
    </CardTable>
  );
};

export default DefaultedMembersTab;
